import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { View, ScrollView, Platform, useWindowDimensions, RefreshControl, TextInput, TouchableOpacity, FlatList } from 'react-native';
import { useRoute, useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { NetworkStackParamList } from '@navigation/types';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { Feather } from '@expo/vector-icons';
import { DefaultRootState } from '@reduxLocal/persistState';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import apis, { sendMessageRequest } from '@api/api';
import DefaultScreen from '@components/Global/DefaultScreenView';
import AvatarHandler from '@components/Global/AvatarHandler/AvatarHandler';
import Button from '@components/UI/Button/Button';
import { 
  H1, 
  H2, 
  H3, 
  H4, 
  H5, 
  SizableText, 
  Text, 
  XStack, 
  YStack, 
  Card, 
  Separator, 
  Tabs,
  View as TamaguiView,
  Input,
  Select,
  Adapt,
  Sheet,
  Spinner,
  Dialog,
  TextArea
} from 'tamagui';
import { MaterialIcons, FontAwesome, Ionicons, AntDesign } from '@expo/vector-icons';
import moment from 'moment';
import { useDeviceType } from '@hooks/useDeviceType';
import toTitleCase from '@hooks/toTitleCase';
import * as Animatable from 'react-native-animatable';
// Interface for the public profile data
interface PublicProfile {
  _id: string;
  profile_information: {
    name: string;
    surname: string;
    avatar: {
      img?: string;
      updatedAt?: string;
    };
    role: string;
    job_title: string;
    department: string;
    company: string;
    city: string;
    country: string;
    skills: string[];
    summary: string;
    website: string;
    social_media: {
      facebook?: string;
      twitter?: string;
      linkedin?: string;
      instagram?: string;
      github?: string;
      website?: string;
    };
  };
  achievements: {
    achievement_points: number;
  };
  status: string;
  profileViews: number;
  memberSince: string;
  lastActive: string;
  isOwnProfile: boolean;
  networking?: {
    company_number: string;
  };
  engagement?: {
    karma: number;
    level: string;
    dataForms: {
      viewsCount: number;
      submissionsCount: number;
      completionsCount: number;
    };
    contributions: {
      totalContributions: number;
      itemsCreated: number;
      itemsShared: number;
    };
    interactions: {
      likesReceived: number;
      commentsReceived: number;
    };
    lastActivity?: string;
  };
}

// Interface for search results
interface SearchResult {
  label: string;
  name: string;
  surname: string;
  email: string;
  value: string;
  avatar: string;
}

// Search filters interface
interface SearchFilters {
  sortBy: 'name' | 'recent' | 'points' | 'views';
  status: 'all' | 'available' | 'busy' | 'away';
  location: string;
}

type ProfilesScreenNavigationProp = StackNavigationProp<NetworkStackParamList, 'Profiles'>;

const ProfilesScreen = () => {
  const route = useRoute();
  const navigation = useNavigation<ProfilesScreenNavigationProp>();
  const theme = themeSelector();
  const { width } = useWindowDimensions();
  const { isTablet } = useDeviceType();
  const currentUser = useSelector((state: DefaultRootState) => state.promiseData?.data.userData);
  
  // Get userId from route params (for individual profile view)
  const { userId } = (route.params as { userId?: string }) || {};

  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState<SearchFilters>({
    sortBy: 'name',
    status: 'all',
    location: ''
  });

  // Message request state
  const [showMessageDialog, setShowMessageDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState<{ id: string; name: string; surname: string } | null>(null);
  const [isSendingMessage, setIsSendingMessage] = useState(false);
  const [messageText, setMessageText] = useState('');
  const [isConnecting, setIsConnecting] = useState(false);
  const queryClient = useQueryClient();
  // Responsive variables
  const isSmallScreen = width < 600;
  const isMediumScreen = width >= 600 && width < 1024;
  const isLargeScreen = width >= 1024;
  const showCompactMode = isSmallScreen;
  const showMediumMode = isMediumScreen;
  const showLargeMode = isLargeScreen;

  // Responsive sizing
  const getResponsiveSize = useCallback((small: number, medium: number, large: number) => {
    if (showCompactMode) return small;
    if (showMediumMode) return medium;
    return large;
  }, [showCompactMode, showMediumMode]);

  const getResponsivePadding = useCallback((small: number, medium: number, large: number) => {
    if (showCompactMode) return small;
    if (showMediumMode) return medium;
    return large;
  }, [showCompactMode, showMediumMode]);

  // Debounced search function
  const debouncedSearch = useCallback(
    (() => {
      let timeoutId: NodeJS.Timeout;
      return (query: string) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(async () => {
          if (query.trim().length >= 2) {
            setIsSearching(true);
            try {
              const results = await apis.searchUsers(query.trim());
              // Filter out current user from search results
              const currentUserId = currentUser?.id || currentUser?._id;
              const filteredResults = (results || []).filter(
                (result: SearchResult) => result.value !== currentUserId
              );
              setSearchResults(filteredResults);
            } catch (error) {
              console.error('Search error:', error);
              setSearchResults([]);
            } finally {
              setIsSearching(false);
            }
          } else {
            setSearchResults([]);
          }
        }, 300);
      };
    })(),
    [currentUser]
  );

  // Handle search input change
  const handleSearchChange = useCallback((text: string) => {
    setSearchQuery(text);
    debouncedSearch(text);
  }, [debouncedSearch]);

  // Handle filter changes
  const handleFilterChange = useCallback((key: keyof SearchFilters, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  }, []);

  // Clear search
  const clearSearch = useCallback(() => {
    setSearchQuery('');
    setSearchResults([]);
  }, []);

  // Navigate to profile
  const navigateToProfile = useCallback((userId: string) => {
    if (__DEV__) {
      console.log('🚀 [DEV] Profile navigation triggered:', {
        userId,
        timestamp: new Date().toISOString(),
        navigationTarget: 'Profiles',
        navigationParams: { userId }
      });
    }

    console.log('Navigating to profile with userId:', userId);
    navigation.navigate('Profiles', { userId });
  }, [navigation]);

  // Fetch public profile data (for individual profile view)
  const { 
    data: profileData, 
    isLoading, 
    error, 
    refetch,
    isRefetching 
  } = useQuery({
    queryKey: ['publicProfile', userId],
    queryFn: async () => {
      if (__DEV__) {
        console.log('🔍 [DEV] Making API call to getPublicProfile with userId:', userId);
        console.log('🔍 [DEV] API endpoint: /users/public-profile/' + userId);
        console.log('🔍 [DEV] Full API call: apis.getPublicProfile(' + userId + ')');
      }

      try {
        const result = await apis.getPublicProfile(userId);

        if (__DEV__) {
          console.log('✅ [DEV] API call successful:', {
            userId,
            hasData: !!result,
            dataKeys: result ? Object.keys(result) : 'no data',
            fullResponse: result,
            responseStructure: {
              hasSuccess: 'success' in result,
              hasData: 'data' in result,
              successValue: result?.success,
              dataValue: result?.data,
              dataType: typeof result?.data,
              dataKeys: result?.data ? Object.keys(result.data) : 'no data'
            }
          });
        }

        return result;
      } catch (apiError) {
        if (__DEV__) {
          console.error('❌ [DEV] API call failed:', {
            userId,
            error: apiError,
            errorMessage: apiError?.message,
            errorResponse: apiError?.response?.data
          });
        }
        throw apiError;
      }
    },
    enabled: !!userId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 3,
  });

  // Transform backend response to match frontend interface
  if (__DEV__ && profileData) {
    console.log('🔄 [DEV] Transforming profile data:', {
      hasProfileData: !!profileData,
      profileDataKeys: profileData ? Object.keys(profileData) : 'no data',
      hasData: !!profileData?.data,
      dataKeys: profileData?.data ? Object.keys(profileData.data) : 'no data',
      success: profileData?.success,
      message: profileData?.message,
      // Debug the actual data structure
      rawData: profileData?.data,
      profileInfo: profileData?.data?.profile_information,
      achievements: profileData?.data?.achievements,
      status: profileData?.data?.status
    });
  }

  const profile: PublicProfile | null = profileData?.data ? {
    _id: profileData.data._id,
    profile_information: {
      name: profileData.data.profile_information?.name || profileData.data.name,
      surname: profileData.data.profile_information?.surname || profileData.data.surname,
      avatar: {
        img: profileData.data.profile_information?.avatar?.img || profileData.data.avatar
      },
      role: profileData.data.profile_information?.role || profileData.data.role,
      job_title: profileData.data.profile_information?.job_title || profileData.data.jobTitle,
      company: profileData.data.profile_information?.company || profileData.data.company,
      department: profileData.data.profile_information?.department || profileData.data.department,
      city: profileData.data.profile_information?.city || profileData.data.location?.city,
      country: profileData.data.profile_information?.country || profileData.data.location?.country,
      skills: profileData.data.profile_information?.skills || profileData.data.skills || [],
      summary: profileData.data.profile_information?.summary || profileData.data.summary,
      website: profileData.data.profile_information?.website || profileData.data.website,
      social_media: {
        linkedin: profileData.data.profile_information?.social_media?.linkedin || profileData.data.socialMedia?.linkedin,
        twitter: profileData.data.profile_information?.social_media?.twitter || profileData.data.socialMedia?.twitter,
        github: profileData.data.profile_information?.social_media?.github || profileData.data.socialMedia?.github
      }
    },
    achievements: {
      achievement_points: profileData.data.achievements?.achievement_points || 0
    },
    status: profileData.data.status || (profileData.data.isActive ? 'active' : 'inactive'),
    profileViews: profileData.data.profileViews || 0,
    memberSince: profileData.data.memberSince,
    lastActive: profileData.data.lastActive || new Date().toISOString(),
    isOwnProfile: false, // Will be determined by comparing with current user
    networking: profileData.data.networking,
    engagement: profileData.data.engagement || {
      karma: 0,
      level: 'newbie',
      dataForms: { viewsCount: 0, submissionsCount: 0, completionsCount: 0 },
      contributions: { totalContributions: 0, itemsCreated: 0, itemsShared: 0 },
      interactions: { likesReceived: 0, commentsReceived: 0 }
    }
  } : null;

  // Debug the final transformed profile
  if (__DEV__) {
    console.log('🎯 [DEV] Final transformed profile:', {
      hasProfile: !!profile,
      profileKeys: profile ? Object.keys(profile) : 'no profile',
      profileInfo: profile?.profile_information ? Object.keys(profile.profile_information) : 'no profile info',
      name: profile?.profile_information?.name,
      surname: profile?.profile_information?.surname
    });
  }

  // Determine if we're in search mode or profile view mode
  const isSearchMode = !userId;
  const isProfileViewMode = !!userId;


  // Handle connection/follow functionality
  const handleConnect = useCallback(async () => {
    if (!profile || !profile._id) return;

    try {
      setIsConnecting(true);

      // Send user invitation (without companyNumber, this creates a 'user' type invitation)
      const result = await apis.addUserToCompany({
        users: [{ id: profile._id }]
      });

      console.log('✅ Connection invitation sent successfully:', {
        targetUser: `${profile.profile_information.name} ${profile.profile_information.surname}`,
        result: result
      });

      // Show success feedback
      if (result?.success) {
        alert('Connection invitation sent successfully!');
      } else if (result?.summary?.alreadyInvited?.length > 0) {
        alert('You have already sent an invitation to this user.');
      } else {
        alert('Connection invitation sent successfully!');
      }

    } catch (error) {
      console.error('❌ Error sending connection invitation:', {
        error: error,
        errorMessage: error?.message,
        errorResponse: error?.response?.data,
        targetUserId: profile._id
      });

      const errorMessage = error?.response?.data?.error ||
        error?.response?.data?.message ||
        'Failed to send connection invitation. Please try again.';
      alert(errorMessage);
    } finally {
      setIsConnecting(false);
      queryClient?.invalidateQueries({ queryKey: ['searchUsers'] });
      queryClient?.invalidateQueries({ queryKey: ['all-users'] });
      //Invalidate message user list query
    }
  }, [profile]);

  const handleMessage = useCallback(() => {
    if (profile) {
      setSelectedUser({
        id: profile._id,
        name: profile.profile_information.name,
        surname: profile.profile_information.surname
      });
      setShowMessageDialog(true);
    }
  }, [profile]);

  const handleShare = useCallback(() => {
    // TODO: Implement share profile logic
    console.log('Share profile:', userId);
  }, [userId]);

  // Handle sending message request
  const handleSendMessageRequest = useCallback(async () => {
    if (!selectedUser) return;

    try {
      setIsSendingMessage(true);

      console.log('🔔 Sending message request:', {
        targetUserId: selectedUser.id,
        message: messageText,
        selectedUser: selectedUser
      });

      const result = await sendMessageRequest(queryClient, selectedUser.id, messageText);

      console.log('✅ Message request sent successfully:', {
        targetUser: selectedUser.name,
        result: result
      });

      // Close dialog and reset state
      setShowMessageDialog(false);
      setSelectedUser(null);
      setMessageText('');

      // You could show a success toast here
      alert('Message request sent successfully!');

    } catch (error) {
      console.error('❌ Error sending message request:', {
        error: error,
        errorMessage: error?.message,
        errorResponse: error?.response?.data,
        targetUserId: selectedUser.id,
        message: messageText
      });
      alert('Failed to send message request. Please try again.');
    } finally {
      setIsSendingMessage(false);
    }
  }, [selectedUser, messageText]);

  // Get status color
  const getStatusColor = useCallback((status: string) => {
    switch (status) {
      case 'available':
        return '#10B981';
      case 'busy':
        return '#F59E0B';
      case 'away':
        return '#EF4444';
      case 'do_not_disturb':
        return '#8B5CF6';
      default:
        return '#6B7280';
    }
  }, []);

  // Get status display text
  const getStatusText = useCallback((status: string) => {
    switch (status) {
      case 'available':
        return 'Available';
      case 'busy':
        return 'Busy';
      case 'away':
        return 'Away';
      case 'do_not_disturb':
        return 'Do Not Disturb';
      default:
        return 'Offline';
    }
  }, []);

  // Get engagement level display info
  const getEngagementLevelInfo = useCallback((level: string) => {
    switch (level) {
      case 'expert':
        return { label: 'Expert', color: '#F59E0B', emoji: '🌟' };
      case 'power':
        return { label: 'Power Contributor', color: '#8B5CF6', emoji: '⭐' };
      case 'contributor':
        return { label: 'Contributor', color: '#3B82F6', emoji: '✨' };
      case 'lurker':
        return { label: 'Lurker', color: '#10B981', emoji: '👀' };
      default:
        return { label: 'Newbie', color: '#6B7280', emoji: '🌱' };
    }
  }, []);

  // Render search result item
  const renderSearchResult = useCallback(({ item }: { item: SearchResult }) => {
    return (
      <Animatable.View animation="fadeInUp" duration={300}>
        <TouchableOpacity
          onPress={() => {
            console.log('Profile tapped:', item);
            navigateToProfile(item.value);
          }}
          activeOpacity={0.7}
        >
          <Card
            backgroundColor={theme ? "#ffffff" : "#1e293b"}
            borderColor={theme ? "#e2e8f0" : "#334155"}
            borderRadius={16}
            padding="$4"
            marginBottom="$2"
            width="100%"
            alignSelf="stretch"
          >
            <XStack gap="$3" alignItems="center">
              <AvatarHandler
                avatar={item.avatar}
                avatarImageStyle={{
                  width: getResponsiveSize(48, 56, 64),
                  height: getResponsiveSize(48, 56, 64),
                  borderRadius: getResponsiveSize(24, 28, 32),
                  borderWidth: 2,
                  borderColor: theme ? '#e2e8f0' : '#334155',
                }}
                replacementImageStyle={{
                  width: getResponsiveSize(48, 56, 64),
                  height: getResponsiveSize(48, 56, 64),
                  borderRadius: getResponsiveSize(24, 28, 32),
                }}
              />

              <YStack flex={1} gap="$1">
                <H4
                  color={theme ? "#1e293b" : "#f1f5f9"}
                  fontWeight="600"
                  fontSize={getResponsiveSize(16, 17, 18)}
                >
                  {item.name} {item.surname}
                </H4>
                <SizableText
                  size="$3"
                  color={theme ? "#64748b" : "#94a3b8"}
                >
                  {item.email}
                </SizableText>
              </YStack>

              <MaterialIcons
                name="chevron-right"
                size={24}
                color={theme ? "#64748b" : "#94a3b8"}
              />
            </XStack>
          </Card>
        </TouchableOpacity>
      </Animatable.View>
    );
  }, [theme, navigateToProfile, getResponsiveSize]);


  // Loading state for profile view
  if (isProfileViewMode && isLoading) {
    return (
      <DefaultScreen
        backButton
        screenTitle="User Profile"
        fullWidth={true}
        noMargin
      >
        <YStack
          flex={1}
          alignItems="center"
          justifyContent="center"
          gap="$4"
          paddingHorizontal="$4"
        >
          <MaterialIcons name="person" size={48} color={theme ? "#64748b" : "#94a3b8"} />
          <SizableText size="$4" color={theme ? "#64748b" : "#94a3b8"}>
            Loading profile...
          </SizableText>
        </YStack>
      </DefaultScreen>
    );
  }

  // Error state for profile view
  if (isProfileViewMode && !isLoading && (error || !profile)) {
    return (
      <DefaultScreen
        backButton
        screenTitle="User Profile"
        fullWidth={true}
        noMargin
      >
        <YStack
          flex={1}
          alignItems="center"
          justifyContent="center"
          gap="$4"
          paddingHorizontal="$4"
        >
          <MaterialIcons name="error-outline" size={48} color="#EF4444" />
          <H3 textAlign="center" color="#EF4444" fontWeight="600">
            Profile Not Found
          </H3>
          <SizableText
            textAlign="center"
            size="$4"
            color={theme ? "#64748b" : "#94a3b8"}
            paddingHorizontal="$4"
          >
            {(error as any)?.response?.data?.error ||
              (error as any)?.response?.data?.message ||
              (error as any)?.message ||
              'This user profile could not be loaded or does not exist.'}
          </SizableText>

          {__DEV__ && error && (
            <Card
              backgroundColor={theme ? "#fef2f2" : "#2d1b1b"}
              borderColor="#dc2626"
              borderWidth={1}
              borderRadius={12}
              padding="$3"
              marginTop="$4"
              width="90%"
            >
              <YStack gap="$2">
                <SizableText size="$2" color="#dc2626" fontWeight="600">
                  DEV Error Details:
                </SizableText>
                <SizableText size="$1" color="#dc2626" fontFamily="monospace">
                  Status: {(error as any)?.response?.status || 'Unknown'}
                </SizableText>
                <SizableText size="$1" color="#dc2626" fontFamily="monospace">
                  Error: {(error as any)?.response?.data?.error || 'Unknown error'}
                </SizableText>
                <SizableText size="$1" color="#dc2626" fontFamily="monospace">
                  Message: {(error as any)?.response?.data?.message || 'No message'}
                </SizableText>
              </YStack>
            </Card>
          )}
          <XStack gap="$3" marginTop="$4" width="90%" flexWrap="wrap">
            <Button
              onPress={() => navigation.goBack()}
              buttonText="Go Back"
              customStyle={{
                backgroundColor: theme ? '#6B7280' : '#4B5563',
                flex: showCompactMode ? 1 : undefined,
                minWidth: showCompactMode ? undefined : 120
              }}
            />
            <Button
              onPress={() => refetch()}
              buttonText="Retry"
              customStyle={{
                backgroundColor: theme ? '#3B82F6' : '#1D4ED8',
                flex: showCompactMode ? 1 : undefined,
                minWidth: showCompactMode ? undefined : 120
              }}
            />
          </XStack>
        </YStack>
      </DefaultScreen>
    );
  }

  // Render search interface
  if (isSearchMode) {
    return (
      <DefaultScreen
        backButton
        screenTitle="Search Users"
        fullWidth={true}
        noMargin
        searchBarStyle={{ marginTop: 10 }}
        searchBar={{
          placeHolder: "Search users by name, email, or company...",
          callback: handleSearchChange,
          searchState: searchQuery,
          setSearchState: setSearchQuery,
          showSearch: true,
          extraComponents: (
            <TouchableOpacity
              onPress={() => setShowFilters(!showFilters)}
              style={{
                padding: 8,
                borderRadius: 8,
                backgroundColor: showFilters
                  ? (theme ? '#3B82F6' : '#1D4ED8')
                  : (theme ? '#f1f5f9' : '#334155'),
              }}
            >
              <MaterialIcons
                name="tune"
                size={20}
                color={showFilters
                  ? "#ffffff"
                  : (theme ? "#64748b" : "#94a3b8")
                }
              />
            </TouchableOpacity>
          )
        }}
      >
        <YStack
          flex={1}
          paddingHorizontal="$4"
          paddingTop="$2"
          alignSelf="stretch"
        >
          <ScrollView
            showsVerticalScrollIndicator={false}
            contentContainerStyle={{ paddingBottom: 40 }}
          >
            <YStack gap="$4" width="100%" alignSelf="stretch">
            {/* Filters */}
            {showFilters && (
              <Animatable.View animation="fadeInDown" duration={300}>
                <Card
                    backgroundColor={theme ? "#ffffff" : "#1e293b"}
                    borderColor={theme ? "#e2e8f0" : "#334155"}
                    borderRadius={16}
                    padding="$4"
                    marginBottom="$4"
                  width="100%"
                  alignSelf="stretch"
                >
                    <YStack gap="$3">
                      <XStack gap="$3" flexWrap="wrap">
                        <YStack flex={1} minWidth={showCompactMode ? "100%" : 150}>
                          <SizableText
                            size="$3"
                            color={theme ? "#64748b" : "#94a3b8"}
                            marginBottom="$1"
                            fontWeight="500"
                          >
                          Sort By
                        </SizableText>
                        <Select
                          value={filters.sortBy}
                          onValueChange={(value) => handleFilterChange('sortBy', value)}
                        >
                          <Select.Trigger
                            width="100%"
                              padding="$3"
                              borderRadius={12}
                            backgroundColor={theme ? "$grey2" : "$grey12"}
                            borderColor={theme ? "$grey6" : "$grey8"}
                          >
                              <Select.Value color={theme ? "#1e293b" : "#f1f5f9"} />
                          </Select.Trigger>
                          <Select.Content zIndex={200000}>
                            <Select.ScrollUpButton />
                            <Select.Viewport>
                              <Select.Item index={0} value="name">
                                <Select.ItemText>Name</Select.ItemText>
                              </Select.Item>
                              <Select.Item index={1} value="recent">
                                <Select.ItemText>Recently Active</Select.ItemText>
                              </Select.Item>
                              <Select.Item index={2} value="points">
                                <Select.ItemText>Achievement Points</Select.ItemText>
                              </Select.Item>
                              <Select.Item index={3} value="views">
                                <Select.ItemText>Profile Views</Select.ItemText>
                              </Select.Item>
                            </Select.Viewport>
                            <Select.ScrollDownButton />
                          </Select.Content>
                        </Select>
                      </YStack>

                        <YStack flex={1} minWidth={showCompactMode ? "100%" : 150}>
                          <SizableText
                            size="$3"
                            color={theme ? "#64748b" : "#94a3b8"}
                            marginBottom="$1"
                            fontWeight="500"
                          >
                          Status
                        </SizableText>
                        <Select
                          value={filters.status}
                          onValueChange={(value) => handleFilterChange('status', value)}
                        >
                          <Select.Trigger
                            width="100%"
                              padding="$3"
                              borderRadius={12}
                            backgroundColor={theme ? "$grey2" : "$grey12"}
                            borderColor={theme ? "$grey6" : "$grey8"}
                          >
                              <Select.Value color={theme ? "#1e293b" : "#f1f5f9"} />
                          </Select.Trigger>
                          <Select.Content zIndex={200000}>
                            <Select.ScrollUpButton />
                            <Select.Viewport>
                              <Select.Item index={0} value="all">
                                <Select.ItemText>All Status</Select.ItemText>
                              </Select.Item>
                              <Select.Item index={1} value="available">
                                <Select.ItemText>Available</Select.ItemText>
                              </Select.Item>
                              <Select.Item index={2} value="busy">
                                <Select.ItemText>Busy</Select.ItemText>
                              </Select.Item>
                              <Select.Item index={3} value="away">
                                <Select.ItemText>Away</Select.ItemText>
                              </Select.Item>
                            </Select.Viewport>
                            <Select.ScrollDownButton />
                          </Select.Content>
                        </Select>
                      </YStack>
                    </XStack>
                  </YStack>
                </Card>
              </Animatable.View>
            )}

            {/* Search Results */}
            {isSearching && (
                <Animatable.View animation="fadeInUp" duration={300}>
                  <Card
                    backgroundColor={theme ? "#ffffff" : "#1e293b"}
                    borderColor={theme ? "#e2e8f0" : "#334155"}
                    borderRadius={16}
                    padding="$5"
                    alignItems="center"
                    width="100%"
                    alignSelf="stretch"
                  >
                    <XStack gap="$3" alignItems="center">
                      <Spinner size="small" color={theme ? "#3B82F6" : "#60A5FA"} />
                      <SizableText size="$4" color={theme ? "#64748b" : "#94a3b8"}>
                        Searching users...
                      </SizableText>
                    </XStack>
                  </Card>
                </Animatable.View>
            )}

            {searchResults.length > 0 && !isSearching && (
                <YStack gap="$3">
                  <XStack gap="$2" flexWrap="wrap" justifyContent="space-between" alignItems="center" paddingHorizontal="$2">
                    <H4 color={theme ? "#1e293b" : "#f1f5f9"} fontWeight="600">
                    {searchResults.length} user{searchResults.length !== 1 ? 's' : ''} found
                  </H4>
                    <SizableText size="$2" color={theme ? "#64748b" : "#94a3b8"}>
                    Tap to view profile
                  </SizableText>
                </XStack>

                <FlatList
                  data={searchResults}
                  renderItem={renderSearchResult}
                  keyExtractor={(item) => item.value}
                  showsVerticalScrollIndicator={false}
                    scrollEnabled={false}
                    nestedScrollEnabled={false}
                />
              </YStack>
            )}

            {searchQuery.length >= 2 && searchResults.length === 0 && !isSearching && (
                <Animatable.View animation="fadeInUp" duration={300}>
                  <Card
                    backgroundColor={theme ? "#ffffff" : "#1e293b"}
                    borderColor={theme ? "#e2e8f0" : "#334155"}
                    borderRadius={16}
                    padding="$5"
                    alignItems="center"
                    width="100%"
                    alignSelf="stretch"
                  >
                    <YStack gap="$3" alignItems="center">
                      <MaterialIcons
                        name="search-off"
                        size={48}
                        color={theme ? "#64748b" : "#94a3b8"}
                      />
                      <H4 color={theme ? "#1e293b" : "#f1f5f9"} textAlign="center" fontWeight="600">
                        No users found
                      </H4>
                      <SizableText
                        size="$4"
                        color={theme ? "#64748b" : "#94a3b8"}
                        textAlign="center"
                      >
                        Try adjusting your search terms or filters
                      </SizableText>
                    </YStack>
                  </Card>
                </Animatable.View>
            )}

            {searchQuery.length === 0 && (
                <Animatable.View animation="fadeInUp" duration={300}>
                  <Card
                    backgroundColor={theme ? "#ffffff" : "#1e293b"}
                    borderColor={theme ? "#e2e8f0" : "#334155"}
                    borderRadius={16}
                    padding="$5"
                    alignItems="center"
                    width="100%"
                    alignSelf="stretch"
                  >
                    <YStack gap="$3" alignItems="center">
                      <MaterialIcons
                        name="search"
                        size={48}
                        color={theme ? "#64748b" : "#94a3b8"}
                      />
                      <H4 color={theme ? "#1e293b" : "#f1f5f9"} textAlign="center" fontWeight="600">
                        Search for Users
                      </H4>
                      <SizableText
                        size="$4"
                        color={theme ? "#64748b" : "#94a3b8"}
                        textAlign="center"
                      >
                        Enter a name, email, or company to find users
                      </SizableText>
                    </YStack>
                  </Card>
                </Animatable.View>
            )}
          </YStack>
        </ScrollView>
        </YStack>
      </DefaultScreen>
    );
  }

  return (
    <DefaultScreen 
      backButton 
      searchBar={{
        placeHolder: "Search...",
        callback: (query) => console.log("Search query:", query),
      }}
      screenTitle={`${profile.profile_information.name} ${profile.profile_information.surname}`}
      fullWidth={true}
      noMargin
    >
      <YStack
        flex={1}
        paddingHorizontal="$4"
      >
        <ScrollView
          showsVerticalScrollIndicator={false}
          contentContainerStyle={{ paddingBottom: 40 }}
          refreshControl={
            <RefreshControl
              refreshing={isRefetching}
              onRefresh={refetch}
              tintColor={theme ? "#3B82F6" : "#60A5FA"}
            />
          }
        >
        {/* Header Card */}
        <Animatable.View animation="fadeInDown" duration={600} style={{ width: '100%' }}>
          <Card
              backgroundColor={theme ? "#ffffff" : "#1e293b"}
              borderColor={theme ? "#e2e8f0" : "#334155"}
            borderRadius={16}
              padding="$5"
              marginBottom="$4"
            width="100%"
            alignSelf="stretch"
          >
              <YStack gap="$5">
              {/* Avatar and Basic Info */}
                <XStack gap="$4" flexWrap={showCompactMode ? "wrap" : "nowrap"}>
                <View style={{ position: 'relative' }}>
                  <AvatarHandler
                    avatar={profile.profile_information.avatar?.img}
                    avatarImageStyle={{
                      width: getResponsiveSize(80, 90, 100),
                      height: getResponsiveSize(80, 90, 100),
                      borderRadius: getResponsiveSize(40, 45, 50),
                      borderWidth: 3,
                      borderColor: theme ? '#e2e8f0' : '#334155',
                    }}
                    replacementImageStyle={{
                      width: getResponsiveSize(80, 90, 100),
                      height: getResponsiveSize(80, 90, 100),
                      borderRadius: getResponsiveSize(40, 45, 50),
                    }}
                  />
                  
                  {/* Status Indicator */}
                  <View style={{
                    position: 'absolute',
                    bottom: 4,
                    right: 4,
                    width: 20,
                    height: 20,
                    borderRadius: 10,
                    backgroundColor: getStatusColor(profile.status),
                    borderWidth: 3,
                    borderColor: theme ? '#ffffff' : '#0f172a',
                  }} />
                </View>

                  <YStack flex={1} gap="$2" minWidth={showCompactMode ? "100%" : undefined}>
                    <YStack gap="$1">
                      <XStack alignItems="center" gap="$2" flexWrap="wrap">
                        <H2
                          color={theme ? "#1e293b" : "#f1f5f9"}
                          fontSize={getResponsiveSize(22, 24, 26)}
                          fontWeight="700"
                        >
                          {profile.profile_information.name} {profile.profile_information.surname}
                        </H2>
                        {profile.engagement && (
                          <XStack alignItems="center" gap="$1.5" paddingHorizontal="$2" paddingVertical="$1"
                            backgroundColor={theme ? '#f1f5f9' : '#1e293b'}
                            borderRadius={12}>
                            <Text style={{ fontSize: 14, fontWeight: '700', color: getEngagementLevelInfo(profile.engagement.level).color }}>
                              {getEngagementLevelInfo(profile.engagement.level).emoji}
                            </Text>
                            <SizableText size="$2" color={getEngagementLevelInfo(profile.engagement.level).color} fontWeight="600">
                              {profile.engagement.karma || 0}
                            </SizableText>
                          </XStack>
                        )}
                      </XStack>
                    
                    {profile.profile_information.job_title && (
                      <H4 
                        color={theme ? "#475569" : "#94a3b8"}
                          fontSize={getResponsiveSize(15, 16, 17)}
                        fontWeight="500"
                      >
                        {profile.profile_information.job_title}
                      </H4>
                    )}
                    
                    {profile.profile_information.company && (
                        <XStack alignItems="center" gap="$1.5">
                        <MaterialIcons 
                          name="business" 
                          size={16} 
                          color={theme ? "#64748b" : "#94a3b8"} 
                        />
                        <SizableText 
                            size="$3" 
                          color={theme ? "#64748b" : "#94a3b8"}
                        >
                          {profile.profile_information.company}
                        </SizableText>
                      </XStack>
                    )}
                    
                    {(profile.profile_information.city || profile.profile_information.country) && (
                        <XStack alignItems="center" gap="$1.5">
                        <MaterialIcons 
                          name="location-on" 
                          size={16} 
                          color={theme ? "#64748b" : "#94a3b8"} 
                        />
                        <SizableText 
                            size="$3" 
                          color={theme ? "#64748b" : "#94a3b8"}
                        >
                          {[profile.profile_information.city, profile.profile_information.country]
                            .filter(Boolean)
                            .join(', ')}
                        </SizableText>
                      </XStack>
                    )}
                  </YStack>

                  {/* Status */}
                    <XStack alignItems="center" gap="$2">
                    <View style={{
                      width: 12,
                      height: 12,
                      borderRadius: 6,
                      backgroundColor: getStatusColor(profile.status),
                    }} />
                    <SizableText 
                        size="$3" 
                      color={theme ? "#64748b" : "#94a3b8"}
                      fontWeight="500"
                    >
                      {getStatusText(profile.status)}
                    </SizableText>
                  </XStack>
                </YStack>
              </XStack>

              {/* Action Buttons */}
              {!profile.isOwnProfile && (
                <XStack
                    gap="$2"
                  flexWrap="wrap"
                  justifyContent={showCompactMode ? "space-between" : "flex-start"}
                >
                  <Button
                    onPress={handleConnect}
                      buttonText={isConnecting ? "Connecting..." : "Connect"}
                      icon="user-add"
                      disabled={isConnecting}
                    customStyle={{
                      flex: showCompactMode ? 1 : undefined,
                      minWidth: showCompactMode ? undefined : 120,
                      backgroundColor: theme ? '#3B82F6' : '#1D4ED8',
                      opacity: isConnecting ? 0.6 : 1,
                    }}
                  />
                  <Button
                    onPress={handleMessage}
                    buttonText="Message"
                    icon="message"
                    customStyle={{
                      flex: showCompactMode ? 1 : undefined,
                      minWidth: showCompactMode ? undefined : 120,
                      backgroundColor: theme ? '#059669' : '#047857',
                    }}
                  />
                  <Button
                    onPress={handleShare}
                    buttonText="Share"
                    icon="share-alt"
                    customStyle={{
                      flex: showCompactMode ? 1 : undefined,
                      minWidth: showCompactMode ? undefined : 100,
                      backgroundColor: theme ? '#7C3AED' : '#5B21B6',
                    }}
                  />
                </XStack>
              )}
            </YStack>
          </Card>
        </Animatable.View>

        {/* Stats Cards */}
        <Animatable.View animation="fadeInUp" duration={600} delay={200} style={{ width: '100%' }}>
          <XStack
              gap="$2"
              marginBottom="$4"
            flexWrap={showCompactMode ? "wrap" : "nowrap"}
            width="100%"
            alignSelf="stretch"
          >
            <Card
              flex={1}
                backgroundColor={theme ? "#ffffff" : "#1e293b"}
                borderColor={theme ? "#e2e8f0" : "#334155"}
                borderRadius={16}
                padding="$3"
              alignItems="center"
                minWidth={showCompactMode ? "48%" : undefined}
            >
              <H3 color={theme ? "#3B82F6" : "#60A5FA"} fontWeight="700" fontSize={getResponsiveSize(18, 20, 22)}>
                {profile.achievements.achievement_points}
              </H3>
                <SizableText size="$2" color={theme ? "#64748b" : "#94a3b8"}>
                Points
              </SizableText>
            </Card>
            
            <Card
              flex={1}
                backgroundColor={theme ? "#ffffff" : "#1e293b"}
                borderColor={theme ? "#e2e8f0" : "#334155"}
                borderRadius={16}
                padding="$3"
              alignItems="center"
                minWidth={showCompactMode ? "48%" : undefined}
            >
              <H3 color={theme ? "#059669" : "#10B981"} fontWeight="700" fontSize={getResponsiveSize(18, 20, 22)}>
                {profile.profileViews}
              </H3>
                <SizableText size="$2" color={theme ? "#64748b" : "#94a3b8"}>
                Views
              </SizableText>
            </Card>
            
            <Card
              flex={1}
                backgroundColor={theme ? "#ffffff" : "#1e293b"}
                borderColor={theme ? "#e2e8f0" : "#334155"}
                borderRadius={16}
                padding="$3"
              alignItems="center"
                minWidth={showCompactMode ? "100%" : undefined}
            >
              <H3 color={theme ? "#7C3AED" : "#A78BFA"} fontWeight="700" fontSize={getResponsiveSize(18, 20, 22)}>
                {moment(profile.memberSince).format('MMM YYYY')}
              </H3>
                <SizableText size="$2" color={theme ? "#64748b" : "#94a3b8"}>
                Joined
              </SizableText>
            </Card>

              {profile.engagement && (
                <Card
                  flex={1}
                  backgroundColor={theme ? "#ffffff" : "#1e293b"}
                  borderColor={theme ? "#e2e8f0" : "#334155"}
                  borderRadius={16}
                  padding="$3"
                  alignItems="center"
                  minWidth={showCompactMode ? "48%" : undefined}
                >
                  <XStack alignItems="center" gap="$1.5">
                    <Text style={{ fontSize: getResponsiveSize(18, 20, 22), fontWeight: '700', color: getEngagementLevelInfo(profile.engagement.level).color }}>
                      {getEngagementLevelInfo(profile.engagement.level).emoji}
                    </Text>
                    <H3 color={getEngagementLevelInfo(profile.engagement.level).color} fontWeight="700" fontSize={getResponsiveSize(18, 20, 22)}>
                      {profile.engagement.karma || 0}
                    </H3>
                  </XStack>
                  <SizableText size="$2" color={theme ? "#64748b" : "#94a3b8"}>
                    Karma
                  </SizableText>
                  <SizableText size="$1" color={getEngagementLevelInfo(profile.engagement.level).color} fontWeight="600" marginTop="$1">
                    {getEngagementLevelInfo(profile.engagement.level).label}
                  </SizableText>
                </Card>
              )}
          </XStack>
        </Animatable.View>

        {/* Content Tabs */}
        <Animatable.View animation="fadeInUp" duration={600} delay={400} style={{ width: '100%' }}>
          <Card
              backgroundColor={theme ? "#ffffff" : "#1e293b"}
              borderColor={theme ? "#e2e8f0" : "#334155"}
            borderRadius={16}
              marginBottom="$4"
            overflow="hidden"
            width="100%"
            alignSelf="stretch"
          >
              <Tabs
                defaultValue="about"
                orientation="horizontal"
                flexDirection="column"
              >
              <Tabs.List 
                backgroundColor={theme ? "$grey2" : "$grey12"} 
                borderRadius={0}
                marginBottom={0}
                  flexDirection="row"
              >
                <Tabs.Tab value="about" flex={1}>
                    <SizableText size="$3" fontWeight="600">About</SizableText>
                </Tabs.Tab>
                <Tabs.Tab value="skills" flex={1}>
                    <SizableText size="$3" fontWeight="600">Skills</SizableText>
                </Tabs.Tab>
                <Tabs.Tab value="contact" flex={1}>
                    <SizableText size="$3" fontWeight="600">Contact</SizableText>
                </Tabs.Tab>
              </Tabs.List>

              {/* About Tab */}
                <Tabs.Content value="about" padding="$5">
                  <YStack gap="$4">
                  {profile.profile_information.summary ? (
                      <YStack gap="$2">
                        <H4 color={theme ? "#1e293b" : "#f1f5f9"} fontWeight="600">Summary</H4>
                      <SizableText 
                          size="$4"
                          lineHeight={22}
                        color={theme ? "#475569" : "#94a3b8"}
                      >
                        {profile.profile_information.summary}
                      </SizableText>
                      </YStack>
                  ) : (
                    <SizableText 
                          size="$4" 
                      color={theme ? "#64748b" : "#94a3b8"}
                      fontStyle="italic"
                    >
                      No summary available
                    </SizableText>
                  )}

                  {profile.profile_information.role && (
                      <YStack gap="$2">
                        <Separator borderColor={theme ? "#e2e8f0" : "#334155"} marginVertical="$2" />
                        <H5 color={theme ? "#1e293b" : "#f1f5f9"} fontWeight="600">Role</H5>
                        <SizableText 
                          size="$4"
                          color={theme ? "#475569" : "#94a3b8"}
                        >
                          {profile.profile_information.role}
                        </SizableText>
                      </YStack>
                  )}

                  {profile.profile_information.department && (
                      <YStack gap="$2">
                        <Separator borderColor={theme ? "#e2e8f0" : "#334155"} marginVertical="$2" />
                        <H5 color={theme ? "#1e293b" : "#f1f5f9"} fontWeight="600">Department</H5>
                        <SizableText 
                          size="$4"
                          color={theme ? "#475569" : "#94a3b8"}
                        >
                          {toTitleCase(profile.profile_information.department)}
                        </SizableText>
                      </YStack>
                  )}
                </YStack>
              </Tabs.Content>

              {/* Skills Tab */}
                <Tabs.Content value="skills" padding="$5">
                  <YStack gap="$4">
                    <H4 color={theme ? "#1e293b" : "#f1f5f9"} fontWeight="600">Skills & Expertise</H4>
                  
                  {profile.profile_information.skills && profile.profile_information.skills.length > 0 ? (
                      <XStack gap="$2" flexWrap="wrap">
                      {profile.profile_information.skills.map((skill, index) => (
                        <View
                          key={index}
                          style={{
                            backgroundColor: theme ? '#f1f5f9' : '#334155',
                            paddingHorizontal: 12,
                            paddingVertical: 6,
                            borderRadius: 20,
                            borderWidth: 1,
                            borderColor: theme ? '#e2e8f0' : '#475569',
                          }}
                        >
                          <SizableText 
                            size="$2" 
                            color={theme ? "#475569" : "#cbd5e1"}
                            fontWeight="500"
                          >
                            {skill}
                          </SizableText>
                        </View>
                      ))}
                    </XStack>
                  ) : (
                    <SizableText 
                          size="$4" 
                      color={theme ? "#64748b" : "#94a3b8"}
                      fontStyle="italic"
                    >
                      No skills listed
                    </SizableText>
                  )}
                </YStack>
              </Tabs.Content>

              {/* Contact Tab */}
                <Tabs.Content value="contact" padding="$5">
                  <YStack gap="$4">
                    <H4 color={theme ? "#1e293b" : "#f1f5f9"} fontWeight="600">Contact Information</H4>
                  
                    <YStack gap="$3">
                    {profile.profile_information.website && (
                        <XStack alignItems="center" gap="$3">
                        <MaterialIcons 
                          name="language" 
                          size={20} 
                          color={theme ? "#3B82F6" : "#60A5FA"} 
                        />
                        <SizableText 
                            size="$4" 
                          color={theme ? "#3B82F6" : "#60A5FA"}
                          textDecorationLine="underline"
                        >
                          {profile.profile_information.website}
                        </SizableText>
                      </XStack>
                    )}

                    {/* Social Media Links */}
                    {Object.entries(profile.profile_information.social_media || {}).map(([platform, url]) => {
                      if (!url) return null;
                      
                      const getIcon = (platform: string) => {
                        switch (platform) {
                          case 'linkedin': return 'linkedin';
                          case 'twitter': return 'twitter';
                          case 'facebook': return 'facebook';
                          case 'instagram': return 'instagram';
                          case 'github': return 'github';
                          default: return 'link';
                        }
                      };

                      return (
                        <XStack key={platform} alignItems="center" gap="$3">
                          <FontAwesome 
                            name={getIcon(platform) as any} 
                            size={20} 
                            color={theme ? "#64748b" : "#94a3b8"} 
                          />
                          <SizableText 
                            size="$4" 
                            color={theme ? "#3B82F6" : "#60A5FA"}
                            textDecorationLine="underline"
                          >
                            {toTitleCase(platform)}
                          </SizableText>
                        </XStack>
                      );
                    })}

                    {(!profile.profile_information.website && 
                      Object.keys(profile.profile_information.social_media || {}).length === 0) && (
                      <SizableText 
                          size="$4" 
                        color={theme ? "#64748b" : "#94a3b8"}
                        fontStyle="italic"
                      >
                        No contact information available
                      </SizableText>
                    )}
                  </YStack>
                </YStack>
              </Tabs.Content>
            </Tabs>
          </Card>
        </Animatable.View>

        {/* Last Active */}
        <Animatable.View animation="fadeInUp" duration={600} delay={600} style={{ width: '100%' }}>
          <Card
              backgroundColor={theme ? "#ffffff" : "#1e293b"}
              borderColor={theme ? "#e2e8f0" : "#334155"}
            borderRadius={16}
              padding="$4"
              marginBottom="$4"
            width="100%"
            alignSelf="stretch"
          >
              <XStack
                alignItems="center"
                justifyContent="space-between"
                flexWrap="wrap"
                gap="$2"
              >
              <SizableText 
                  size="$3" 
                color={theme ? "#64748b" : "#94a3b8"}
              >
                Last active: {moment(profile.lastActive).fromNow()}
              </SizableText>
              
              {profile.networking?.company_number && (
                  <XStack alignItems="center" gap="$1.5">
                  <MaterialIcons 
                    name="verified" 
                    size={16} 
                    color={theme ? "#059669" : "#10B981"} 
                  />
                  <SizableText 
                      size="$2" 
                    color={theme ? "#059669" : "#10B981"}
                    fontWeight="500"
                  >
                    Verified
                  </SizableText>
                </XStack>
              )}
            </XStack>
          </Card>
        </Animatable.View>
        </ScrollView>
      </YStack>

      {/* Message Request Dialog */}
      <Dialog modal open={showMessageDialog} onOpenChange={setShowMessageDialog}>
        <Dialog.Portal>
          <Dialog.Overlay
            key="overlay"
            animation="quick"
            opacity={0.5}
            enterStyle={{ opacity: 0 }}
            exitStyle={{ opacity: 0 }}
          />
          <Dialog.Content
            elevate
            key="content"
            animation={[
              'quick',
              {
                opacity: {
                  overshootClamping: true,
                },
              },
            ]}
            enterStyle={{ x: 0, y: -20, opacity: 0, scale: 0.9 }}
            exitStyle={{ x: 0, y: 10, opacity: 0, scale: 0.95 }}
            x={0}
            scale={1}
            opacity={1}
            y={0}
            maxWidth={500}
            width="90%"
          >
            <YStack gap="$4">
              <Dialog.Title fontSize={18} fontWeight="600" color={theme ? "#1e293b" : "#f1f5f9"}>
                Send Message Request to {selectedUser?.name} {selectedUser?.surname}
              </Dialog.Title>

              <Dialog.Description size="$4" color={theme ? "#64748b" : "#94a3b8"}>
                Add a personal message to your request (optional)
              </Dialog.Description>

              <YStack gap="$3">
                <TextArea
                  placeholder="Add a personal message to your request..."
                  value={messageText}
                  onChangeText={setMessageText}
                  backgroundColor={theme ? "$grey2" : "$grey12"}
                  borderColor={theme ? "$grey6" : "$grey8"}
                  color={theme ? "#1e293b" : "#f1f5f9"}
                  placeholderTextColor={theme ? "#64748b" : "#94a3b8"}
                  fontSize={14}
                  padding="$3"
                  borderRadius={12}
                  minHeight={100}
                  maxHeight={200}
                />

                <SizableText size="$2" color={theme ? "#64748b" : "#94a3b8"}>
                  Let them know why you'd like to connect and start a conversation.
                </SizableText>
              </YStack>

              <XStack gap="$3" justifyContent="flex-end">
                <Dialog.Close displayWhenAdapted asChild>
                  <Button
                    onPress={() => {
                      setShowMessageDialog(false);
                      setMessageText('');
                      setSelectedUser(null);
                    }}
                    customStyle={{
                      backgroundColor: theme ? '#6B7280' : '#4B5563'
                    }}
                  >
                    Cancel
                  </Button>
                </Dialog.Close>

                <Button
                  onPress={handleSendMessageRequest}
                  disabled={isSendingMessage}
                  customStyle={{
                    backgroundColor: theme ? '#3B82F6' : '#1D4ED8'
                  }}
                >
                  {isSendingMessage ? "Sending..." : "Send Request"}
                </Button>
              </XStack>
            </YStack>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog>
    </DefaultScreen>
  );
};

export default ProfilesScreen;
